import React from "react";
import { useTranslation } from "react-i18next";
import { Props } from "./types";
import styles from "./styles.scss";

const CONTAINER_SIZE = 300;
const CIRCLE_SIZE = 240;
const MARGIN = (CONTAINER_SIZE - CIRCLE_SIZE) / 2;
const OUTPUT_SIZE = 512;

function ImageCropper({ source, fileName, onConfirm, onCancel }: Props) {
  const { t } = useTranslation();
  const [image, setImage] = React.useState<HTMLImageElement | null>(null);
  const [scale, setScale] = React.useState(1);
  const [offset, setOffset] = React.useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = React.useState(false);
  const dragStart = React.useRef({ x: 0, y: 0 });
  const offsetStart = React.useRef({ x: 0, y: 0 });
  const didDrag = React.useRef(false);

  React.useEffect(() => {
    const img = new Image();
    img.onload = () => {
      setImage(img);
      const initialScale = CIRCLE_SIZE / Math.min(img.width, img.height);
      setScale(initialScale);
      setOffset({
        x: MARGIN + (CIRCLE_SIZE - img.width * initialScale) / 2,
        y: MARGIN + (CIRCLE_SIZE - img.height * initialScale) / 2,
      });
    };
    img.src = source;
  }, [source]);

  const minScale = image
    ? CIRCLE_SIZE / Math.min(image.width, image.height)
    : 1;

  const clampOffset = (
    x: number,
    y: number,
    s: number,
  ): { x: number; y: number } => {
    if (!image) return { x, y };
    const w = image.width * s;
    const h = image.height * s;
    return {
      x: Math.min(MARGIN, Math.max(MARGIN + CIRCLE_SIZE - w, x)),
      y: Math.min(MARGIN, Math.max(MARGIN + CIRCLE_SIZE - h, y)),
    };
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    didDrag.current = false;
    setIsDragging(true);
    dragStart.current = { x: e.clientX, y: e.clientY };
    offsetStart.current = { ...offset };
  };

  React.useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      didDrag.current = true;
      const dx = e.clientX - dragStart.current.x;
      const dy = e.clientY - dragStart.current.y;
      setOffset(
        clampOffset(
          offsetStart.current.x + dx,
          offsetStart.current.y + dy,
          scale,
        ),
      );
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      requestAnimationFrame(() => {
        didDrag.current = false;
      });
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, scale]);

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.05 : 0.05;
    const nextScale = Math.max(minScale, Math.min(minScale * 5, scale + delta));
    const ratio = nextScale / scale;
    const cx = CONTAINER_SIZE / 2;
    const cy = CONTAINER_SIZE / 2;
    const nextX = cx - (cx - offset.x) * ratio;
    const nextY = cy - (cy - offset.y) * ratio;
    setScale(nextScale);
    setOffset(clampOffset(nextX, nextY, nextScale));
  };

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nextScale = parseFloat(e.target.value);
    const ratio = nextScale / scale;
    const cx = CONTAINER_SIZE / 2;
    const cy = CONTAINER_SIZE / 2;
    const nextX = cx - (cx - offset.x) * ratio;
    const nextY = cy - (cy - offset.y) * ratio;
    setScale(nextScale);
    setOffset(clampOffset(nextX, nextY, nextScale));
  };

  const handleConfirm = () => {
    if (!image) return;

    const canvas = document.createElement("canvas");
    canvas.width = OUTPUT_SIZE;
    canvas.height = OUTPUT_SIZE;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.beginPath();
    ctx.arc(OUTPUT_SIZE / 2, OUTPUT_SIZE / 2, OUTPUT_SIZE / 2, 0, Math.PI * 2);
    ctx.closePath();
    ctx.clip();

    const ratio = OUTPUT_SIZE / CIRCLE_SIZE;
    ctx.drawImage(
      image,
      (offset.x - MARGIN) * ratio,
      (offset.y - MARGIN) * ratio,
      image.width * scale * ratio,
      image.height * scale * ratio,
    );

    canvas.toBlob((blob) => {
      if (!blob) return;
      const uri = URL.createObjectURL(blob);
      onConfirm({
        uri,
        type: "image/png",
        size: blob.size,
        name: fileName,
      });
    }, "image/png");
  };

  if (!image) return null;

  return (
    <div
      className={styles.overlay}
      onClick={(e) =>
        e.target === e.currentTarget && !didDrag.current && onCancel()
      }
    >
      <div className={styles.modal}>
        <div className={styles.header}>
          <h3 className={styles.title}>{t("imageCropper.title")}</h3>
        </div>
        <div className={styles.body}>
          <div
            className={styles.canvas}
            onMouseDown={handleMouseDown}
            onWheel={handleWheel}
          >
            <img
              className={styles.image}
              src={source}
              alt=""
              draggable={false}
              style={{
                transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
              }}
            />
            <div className={styles.mask} />
          </div>
          <div className={styles.controls}>
            <p className={styles.hint}>{t("imageCropper.hint")}</p>
            <div className={styles.zoomControls}>
              <span className={styles.zoomLabel}>-</span>
              <input
                type="range"
                className={styles.slider}
                min={minScale}
                max={minScale * 5}
                step={0.01}
                value={scale}
                onChange={handleSliderChange}
              />
              <span className={styles.zoomLabel}>+</span>
            </div>
          </div>
        </div>
        <div className={styles.footer}>
          <button className={styles.cancelButton} onClick={onCancel}>
            {t("imageCropper.cancel")}
          </button>
          <button className={styles.confirmButton} onClick={handleConfirm}>
            {t("imageCropper.confirm")}
          </button>
        </div>
      </div>
    </div>
  );
}

export { ImageCropper };
